import { useEffect, useMemo } from 'react'

import { useRequest, useGasFetcher } from 'hooks/api'
import { useCopy } from 'hooks/helpers'
import { useBalances } from 'hooks/wallet'
import { useSendRefund } from 'hooks/airdrop'
import { useNativeCurrency } from 'hooks/token'

import { useIsPendingRefundTx, usePrivateKey, useSelfGeneratedAddress } from 'state/user/hooks'

import { fromWei } from 'utils/crypto'
import { formatPrice } from 'utils/format'
import { etherNumbers } from 'constants/variables'

import { useCallNotification } from 'notifications/hooks'
import { NotificationTypes } from 'notifications/service'
import { Button, Icon, Notification } from 'components/ui'

import { SelfItem, SelfItemText, ButtonCopyItem } from './Style'

export function RefundInfo() {
  const privateKey = usePrivateKey()
  const nativeCurrency = useNativeCurrency()
  const privateKeyAddress = useSelfGeneratedAddress()

  const getGasParams = useGasFetcher()
  const balances = useBalances(privateKeyAddress)

  const sendRefund = useSendRefund()
  const refundRequest = useRequest(sendRefund)
  const callNotification = useCallNotification()

  const pendingRefundTx = useIsPendingRefundTx()

  const [isCopied, onClick] = useCopy(privateKey || '')

  useEffect(() => {
    if (refundRequest.errorMessage) {
      callNotification({ type: NotificationTypes.ERROR, message: refundRequest.errorMessage })
    }
  }, [refundRequest.errorMessage])

  const handleRefund = () => {
    refundRequest.call()
  }

  const gasPrice = useMemo(() => getGasParams.cost || etherNumbers.BG_ZERO, [getGasParams.cost])

  const isSelfGeneratedHasBalance = useMemo(() => {
    return balances.nativeBalance ? BigInt(balances.nativeBalance) > etherNumbers.BG_ZERO : false
  }, [balances.nativeBalance])

  const selfGeneratedBalance = useMemo(() => {
    return balances.nativeBalance ? formatPrice(fromWei(balances.nativeBalance), 6) : '0'
  }, [balances.nativeBalance])

  const isDisabledRefund = useMemo(() => {
    const gasLimit = etherNumbers.TRANSFER_GAS_LIMIT

    return BigInt(balances.nativeBalance) < gasLimit * gasPrice
  }, [balances.nativeBalance, gasPrice])

  const isDisabled = useMemo(() => {
    return pendingRefundTx || isDisabledRefund
  }, [pendingRefundTx, isDisabledRefund])

  const isLoading = useMemo(() => {
    return pendingRefundTx || refundRequest.isLoading
  }, [pendingRefundTx, refundRequest.isLoading])

  return isSelfGeneratedHasBalance ? (
    <>
      <Notification
        type='info'
        margin='1.6rem auto'
        message={`Your self-generated account has funds (${selfGeneratedBalance} ${nativeCurrency.symbol}). Make refund or make sure that you have saved the private key`}
      />

      <SelfItem>
        <SelfItemText>_private-key</SelfItemText>

        <ButtonCopyItem type='primary' onClick={onClick}>
          ***********************
          {isCopied ? <span>_copied</span> : <Icon name='copy' size='extraMedium' />}
        </ButtonCopyItem>
        <Button fullSize size='large' onClick={handleRefund} disabled={isDisabled} isLoading={isLoading}>
          _refund
        </Button>

        {isDisabledRefund && (
          <Notification
            type='warning'
            margin='0 auto 1.6rem'
            message='Transaction fee exceeds the balance of your private wallet. Please, save the private key and try to make
              refund later, when transaction fee will decrease.'
          />
        )}

        {pendingRefundTx && (
          <Notification type='warning' margin='0 auto 1.6rem' message='Refund transaction is already processing.' />
        )}
      </SelfItem>
    </>
  ) : null
}
